import * as React from 'react';
import { connect } from 'react-redux';

import {
    AccountingPackage,
    PortalTextCommon,
    PortalTextImportModals,
    ShareableLink,
} from '../../Models/Api/strongbox.financialportal';
import { UniversalCollectorAccountingPackageNameId } from '../../Models/Api/AccountingPackage';
import { ApplicationState } from '../../Store';
import { GetFilteredAccountingPackages } from '../../Store/AppSettings';
import { actionCreators as NewDataCollectionActionCreators } from '../../Store/NewDataCollectionParameters';
import { GetPortalText } from '../../Store/PortalAssets';

import { Overlay } from '../Overlay/Overlay';
import { Button, ModalFooter, ModalHeader, Tag } from '../design-system/components';
import { DiscardProgressModal } from '../DiscardProgressModal/DiscardProgressModal';
import '../ImportCustomerFinancials/NewConnectionModal.scss';

type InjectedReduxState = {
    commonText: PortalTextCommon | undefined;
    importModalsText: PortalTextImportModals | undefined;
    accountingPackages: AccountingPackage[];
};

type InjectedActionCreators = typeof NewDataCollectionActionCreators;

type LenderLinkModalProps = {
    workspaceName: string;
    onClose?: () => void;
    linkDetails: Pick<ShareableLink, 'url' | 'expirationTime'> | null;
};

type Props = LenderLinkModalProps & InjectedReduxState & InjectedActionCreators;

// The V2 "Select an accounting system" picker: a single-select list of the accounting packages configured
// for the running consumer (GetFilteredAccountingPackages), styled to the design-system modal from the
// prototype. Choosing a row and pressing Next selects that package (SetSelectedAccountingPackage), which
// advances the store-driven flow to the connect confirmation. Cancel aborts the flow.
const AccountingSystemSelectorModalComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        commonText,
        importModalsText,
        accountingPackages,
        onClose,
        SetSelectedAccountingPackage,
    } = props;

    const [selectedNameId, setSelectedNameId] = React.useState<string | null>(null);
    const [showDiscardConfirm, setShowDiscardConfirm] = React.useState<boolean>(false);

    // Real packages for this consumer, excluding the universal collector (handled by its own flow), ordered
    // the way marketing wants. featureName passed to SetSelectedAccountingPackage is the package nameId.
    const systems = React.useMemo(() => {
        return accountingPackages
            .filter(pkg => pkg.nameId !== UniversalCollectorAccountingPackageNameId)
            .sort((p1, p2) => (p1.marketingSortOrder ?? 0) - (p2.marketingSortOrder ?? 0))
            .map(pkg => ({ nameId: pkg.nameId ?? '', label: pkg.friendlyName ?? '', isBeta: !!pkg.isBeta }))
            .filter(system => !!system.nameId);
    }, [accountingPackages]);

    const onCloseModal = () => onClose && onClose();
    // Cancelling this picker prompts the "Discard progress?" confirmation (it is always past the first page).
    const requestClose = () => setShowDiscardConfirm(true);
    const onNext = () => {
        if (selectedNameId) {
            SetSelectedAccountingPackage(selectedNameId);
        }
    };

    return (
        <>
        <Overlay>
            <div className={'cr-modal page-modal'} style={{ width: 800 }}>
                <ModalHeader title={importModalsText?.selectAccountingSystemLender || ''} onClose={requestClose} />
                <div className={'cr-modal-body page-modal-body'}>
                    <div className={'page-stack page-stack--tight'}>
                        {systems.map(system => (
                            <button
                                key={system.nameId}
                                type={'button'}
                                className={`system-item${selectedNameId === system.nameId ? ' system-item--selected' : ''}`}
                                onClick={() => setSelectedNameId(system.nameId)}
                            >
                                {system.label}
                                {system.isBeta && (
                                    <Tag
                                        type={'secondary'}
                                        outlined
                                        style={{ marginLeft: 6, verticalAlign: 'middle' }}
                                    >
                                        Beta
                                    </Tag>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                <ModalFooter
                    tertiaryAction={
                        <Button type={'floating'} onClick={requestClose}>{commonText?.cancel || ''}</Button>
                    }
                >
                    <Button
                        type={'primary'}
                        className={'page-btn--min-width'}
                        disabled={!selectedNameId}
                        onClick={onNext}
                    >
                        {commonText?.nextButton || ''}
                    </Button>
                </ModalFooter>
            </div>
        </Overlay>
        {showDiscardConfirm && (
            <DiscardProgressModal
                onKeepEditing={() => setShowDiscardConfirm(false)}
                onDiscard={() => {
                    setShowDiscardConfirm(false);
                    onCloseModal();
                }}
            />
        )}
        </>
    );
};

export const AccountingSystemSelectorModal = connect<InjectedReduxState, InjectedActionCreators, LenderLinkModalProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            importModalsText: GetPortalText<PortalTextImportModals>(appState, 'importModals'),
            accountingPackages: GetFilteredAccountingPackages(appState),
        };
        return result;
    },
    {
        ...NewDataCollectionActionCreators
    }
)(AccountingSystemSelectorModalComponent as any);
